import React from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { useState } from 'react';
import MyDataTable from '../../../components/MyDataTable';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { GuardianAppParamList } from '../../../typings/Params';
import { guardianAppService } from '../../../services/central-api/guardian-app';
import Toast from 'react-native-toast-message';
import { ClientError, GenericPaginatedParameters } from '../../../services/central-api/base-service';
import { TextMask } from 'react-native-masked-text';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import AlertModal from '../../../components/AlertModal';
import { TranslateRich } from '../../../components/TranslateRich';
import Badge from '../../../components/Badge';

export default function GuardianAppUsers() {
    const { theme } = useStyles(styleSheet);

    const [selectedGuardianWomanToDelete, setSelectedGuardianWomanToDelete] = useState<GuardianWoman | null>(null);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const navigation = useNavigation<NavigationProp<GuardianAppParamList>>();

    const route = useRoute<RouteProp<GuardianAppParamList, 'GuardianAppUsers'>>();

    const [filters, setFilters] = useState<GenericPaginatedParameters>({
        page: Math.max(route.params.page - 1, 0),
        textFilter: route.params.textFilter ?? '',
        limit: Number(route.params.limit) || 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const isFocused = useIsFocused();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getPaginatedGuardianWoman', filters],
        queryFn: () => guardianAppService.getPaginatedGuardianWoman(filters),
        enabled: isFocused,
    });

    async function confirmDelete(guardianWoman: GuardianWoman) {
        try {
            await guardianAppService.deleteGuardianWoman(guardianWoman.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setSelectedGuardianWomanToDelete(null);
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('supportedPeople')}
            actions={<CustomButton
                onPress={() => navigation.navigate('GuardianAppUser', {})}
                text={translate('register')}
            />}
        />
        <Card>
            <DataTableHeader
                title={translate('registeredGuardianAppPeople')}
                zIndex={2}
                filterInput={<FilterInput
                    value={textFilter ?? ''}
                    onChange={text => {
                        setTextFilter(text);
                    }}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    progressPending={isLoading}
                    columns={[{
                        name: translate('status'),
                        cell: row => <View style={{ minWidth: 120, justifyContent: 'center', alignItems: 'flex-start' }}>
                            <Badge
                                mode='SQUARE'
                                color={row.status === 'active' ? 'green' : 'orange'}
                            >{translate(row.status)}</Badge>
                        </View>,
                        maxWidth: '130px',
                        minWidth: '130px',
                    },
                    {
                        name: translate('phone'),
                        cell: row => <View style={{ width: '100%' }}>
                            <TextMask
                                style={{ color: theme.colors.textColor, fontFamily: 'Open Sans' }}
                                type={'cel-phone'}
                                value={row.phone ?? undefined} />
                        </View>,
                        wrap: true,
                        minWidth: '150px',
                        maxWidth: '150px'
                    },
                    {
                        name: translate('name'),
                        selector: row => row.name ?? '',
                        wrap: true,
                    },
                    {
                        name: translate('document'),
                        selector: row => row.document ? row.document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') : '',
                        wrap: true,
                    },
                    {
                        name: translate('address'),
                        selector: row => row.address ?? '',
                        wrap: true,
                    },
                    {
                        button: true,
                        cell: row =>
                            <DropdownButton
                                actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        navigation.navigate('GuardianAppUser', { id: row.id });
                                    }
                                }, {
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedGuardianWomanToDelete(row);
                                        setDeleteModalVisible(true);
                                    },
                                }]}
                            />
                    }]}
                    data={data?.rows || []}
                />}
                pagination={<Pagination
                    totalItems={data?.count || 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>

        <AlertModal
            setModalVisible={setDeleteModalVisible}
            isModalVisible={isDeleteModalVisible}
            type='destructive'
            title={translate('deleteGuardianAppPeople')}
            description={<>
                <TranslateRich
                    i18nKey='deleteSupportedPersonDescription'
                    values={{
                        supportedPersonName: <strong>{selectedGuardianWomanToDelete?.name || ''}</strong>,
                    }}
                />
                <br />
                <br />
                <TranslateRich i18nKey='proceedWithDeletion' />
            </>}
            customButtonLabel={translate('yesDelete')}
            onSubmit={async () => {
                if (selectedGuardianWomanToDelete) {
                    await confirmDelete(selectedGuardianWomanToDelete);
                }
            }}
        />
    </Container>;
}

const styleSheet = createStyleSheet(() => ({}));
